export type { FeatureStatus, FeatureResult } from './feature-flags.types';
import type { FeatureStatus } from './feature-flags.types';

export const VALID_STATUSES: readonly FeatureStatus[] = [
  'enabled',
  'coming_soon',
  'disabled',
  'hidden',
  'maintenance',
];

/**
 * Payload keys starting with this prefix are treated as sub-flags (nested toggles
 * under a module flag). Use names like `sub_my_orders`, `sub_order_book_by_carrier`.
 */
export const SUB_FLAG_PREFIX = 'sub_';

/**
 * Feature flag registry — single source of truth for all flags.
 *
 * Each entry mirrors the `FeatureResult` shape:
 *
 * - `desc`     — human-readable description
 * - `status`   — default status (`FeatureStatus` string or `boolean`: `true` = enabled, `false` = disabled)
 * - `payload`  — optional default payload; keys starting with `SUB_FLAG_PREFIX` are type-safe sub-flags,
 *                everything else is config data returned in `FeatureResult.payload`
 *
 * ## Naming convention
 *
 * See `docs/posthog.md` (Feature Flags → Naming convention). Summary:
 *
 *   `[name]_module`      — module umbrella; disable whole area or partly via `sub_*` in payload
 *   `new_[name]`         — new feature / surface; target explicitly; promote into a module when default
 *   `sub_[name]`         — sub-feature key inside a module payload only (not a top-level flag)
 *   `[area]_[feature]`   — standalone flag (independent PostHog targeting)
 *   `temp_[name]`        — temporary flag (rollout, one-off); remove after ship or merge
 */
export const FEATURE_FLAGS = {
  // --- Auth (web dashboards) ---
  auth_module: {
    desc: 'Authentication entry (sign in, new registration, sign out)',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_sign_in: true,
      sub_new_sign_up: true,
      sub_sign_out: true,
    },
  },
  // --- Payment ---
  payment_module: {
    desc: 'Payment page/flow',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_bank_transfer: true,
    },
  },

  // --- Profile ---
  profile_module: { desc: 'Profile page', status: 'enabled' as FeatureStatus },
  profile_edit: { desc: 'Allow editing profile fields', status: true },

  // --- Analytics ---
  analytics_module: { desc: 'Analytics page', status: 'enabled' as FeatureStatus },

  // --- Image ---
  image_upload: { desc: 'Allow image uploads', status: true },

  // --- Order ---
  order_module: {
    desc: 'Order flow',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_my_orders: true,
      sub_order_book_by_carrier: true,
      sub_order_status_change_by_carrier: true,
      sub_order_photo_upload: true,
      sub_order_create: true,
      sub_order_tracking: true,
      sub_published_orders: true,
      sub_edit_order: true,
    },
  },

  // --- Order v2 (Order + embedded contracts + Offer + Agreement model) ---
  // Phase 3 staging-only flag. Gates the new carrier-dashboard create-order
  // wizard, offer inbox, broker accept/dispatch, counter-proposal UX and the
  // intermediary load detail view. Stays OFF in production until the Phase 7
  // coordinated cutover when customer-dashboard / admin-panel / mobile apps
  // have also adopted the new shape.
  order_v2_module: {
    desc: 'New Order + Offer + Contracts model (staging proving ground)',
    status: 'disabled' as FeatureStatus,
    payload: {
      sub_carrier_create_order: true,
      sub_offer_inbox: true,
      sub_broker_accept: true,
      sub_counter_proposal: true,
      sub_intermediary_view: true,
      sub_public_load_board: false,
    },
  },

  // --- Wallet ---
  wallet_module: {
    desc: 'Wallet page and transactions',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_wallet_balance: true,
      sub_fund_wallet: true,
      sub_wallet_transactions: true,
    },
  },
  // --- Invoice ---
  invoice_module: {
    desc: 'Invoices list and detail',
    status: 'enabled' as FeatureStatus,
    payload: { sub_create_invoice: true, sub_edit_invoice: true },
  },

  // --- Fleet (carrier side) ---
  fleet_module: { desc: 'Fleet/vehicles management', status: 'enabled' as FeatureStatus },

  vehicle_module: {
    desc: 'Vehicle management',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_create_vehicle: true,
      sub_edit_vehicle: true,
    },
  },

  driver_module: {
    desc: 'Driver management',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_create_driver: true,
      sub_edit_driver: true,
    },
  },

  // --- Members (both sides) ---
  members_module: {
    desc: 'Members list and detail',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_members_list: true,
      sub_invite_new_member: true,
    },
  },
  // --- Saved Locations (customer side) ---
  saved_location_module: {
    desc: 'Saved locations/spots',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_create_saved_location: true,
      sub_edit_saved_location: true,
    },
  },

  // --- Customer Management (customer side) ---
  customer_module: {
    desc: 'Customer list management',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_create_customer: true,
      sub_edit_customer: true,
    },
  },

  // --- Contact (customer side) ---
  contact_module: {
    desc: 'Contacts management',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_create_contact: true,
      sub_edit_contact: true,
    },
  },

  // --- CRM (carrier side: single grouped page with Customers / Contacts / SavedLocations / Agreements tabs) ---
  crm_module: {
    desc: 'CRM page — sidebar tab and /crm URL visibility (carrier-dashboard).',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_agreements: true,
    },
  },

  // --- More / Settings shell (web dashboards — tab + `/settings` route; screens inside use their own flags) ---
  more_screen: {
    desc: 'More / Settings area — sidebar tab and /settings URL visibility only',
    status: 'enabled' as FeatureStatus,
  },

  // --- Apps & Integrations marketplace (More card + /more/apps route) ---
  apps_module: {
    desc: 'Apps & Integrations marketplace — More card and /more/apps URL visibility',
    status: 'enabled' as FeatureStatus,
  },

  // --- Billing & subscriptions (Hero-as-seller pages: /billing/*) ---
  // Gates the carrier-dashboard More card and every `/billing/*` route
  // (overview, history, invoice detail, plan picker, addons, saved
  // payment method). Flip the parent to `coming_soon` / `maintenance`
  // / `hidden` / `disabled` from PostHog to roll the whole surface
  // back without a code change.
  billing_module: {
    desc: 'Billing & subscription — More card and /billing/* URL visibility',
    status: 'enabled' as FeatureStatus,
  },

  // --- Embedded financing (pilot) ---
  // Gates the carrier `/financing` landing, the public lender inbox at
  // `/financing/lender`, the home-dashboard CTA banner, the Apply
  // wizard, and tryhero.io marketing copy/sections. Sub-flags share the
  // parent's PostHog targeting (pilot cohort) — flip the parent to
  // `coming_soon` / `maintenance` / `hidden` to roll the whole product
  // back without touching code.
  financing_module: {
    desc: 'Embedded financing — carrier landing, apply wizard, lender inbox, home banner, marketing landing',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_carrier_apply: true,
      sub_lender_inbox: true,
      sub_home_banner: true,
      sub_landing: 'hidden' as FeatureStatus,
    },
  },

  // --- Hero AI agent ---
  // Gates the unified chat panel launcher (and full-page /ai surface) in
  // the dashboard. While the agent is in pre-MVP staging-only mode the
  // flag stays `disabled` in production; staging targets the internal cohort
  // running the integration tests.
  ai_agent_module: {
    desc: 'Hero AI assistant — chat panel launcher, /ai page, and SSE streaming endpoints',
    status: 'disabled' as FeatureStatus,
    payload: {
      sub_chat_panel: true,
      sub_full_page: true,
      sub_order_creation: true,
      sub_assign_driver: true,
    },
  },

  // --- Bayan / Logisti government e-waybill ---
  // Gates the Logisti connect card in Apps & Integrations, the "issue وثيقة
  // النقل" order action, and the waybill panel in the order drawer. Stays
  // `disabled` until the connector has been exercised end to end against
  // ELM — issuing a وثيقة creates a real government record, so the default
  // has to be off rather than on.
  //
  // Turning this off also refuses connecting and manual issuance on the
  // BACKEND — REST and the agent both, through `BayanFeatureGate` — so it is a
  // real gate and not only a UI one. What it deliberately does NOT stop is
  // anything a filed document depends on: trigger-based auto-issuance for a
  // tenant already enabled, closing, cancelling, reads, and the retry sweep. A
  // وثيقة is a government record, and a rollout flag must not strand one in a
  // state nobody can close.
  //
  // Two switches sit below this one. `BAYAN_APP_ID` / `BAYAN_APP_KEY` unset is
  // the hard kill — nothing can call ELM at all, auto-issuance included. And
  // per tenant, an `AppInstalled` row with `enabled: false` stops every
  // document for that tenant, including the ones Hero would have filed on its
  // own licence.
  bayan_integration: {
    desc: 'Bayan (Logisti) government e-waybill — connect card, order action, waybill panel',
    status: 'disabled' as FeatureStatus,
  },

  // --- Wathiqa Plus white-label onboarding funnel ---
  // The kill switch for cold outreach on the Wathiqa Plus WhatsApp line.
  // Off means `POST /agent/whatsapp/bulk-send` refuses the funnel's
  // marketing templates — inbound conversations still work, so a
  // prospect mid-funnel is never stranded by flipping it.
  //
  // Backend-only (no dashboard surface), and off in production until
  // the launch gate in `docs/ai/wathiqa-plus-agent.md` is green:
  // ELM production credentials, approved templates, a tested STOP path,
  // and one pilot org through the whole funnel on staging.
  //
  // The KEY keeps its `logisti_plus_agent` spelling on purpose: it is a
  // PostHog-side identifier that dashboards, cohorts, and saved insights
  // already reference. The product is Wathiqa Plus; the flag key is a
  // wire name, not a brand.
  logisti_plus_agent: {
    desc: 'Wathiqa Plus funnel — cold outreach batches on the white-label WhatsApp line',
    status: 'disabled' as FeatureStatus,
  },

  // Multivariate experiment: Wathiqa Plus credit-ladder prices + tax
  // interpretation per audience, plus the signup free-credit policy
  // (`freeCredits`). Payload is config, not a kill switch — missing /
  // invalid payload falls back to SUBSCRIPTION_CONFIG. Independent of
  // `logisti_plus_agent` (cold-outreach gate).
  //
  // `freeCredits.mode` is `repayable` (the classic try-once-then-pay
  // overdraft) or `free` (a true gift); `freeCredits.qty` is how many;
  // `freeCredits.expiresInDays` is optional (omit = lasts until spent).
  //
  // The sales config rides the same payload, per audience. `plans` is
  // what is sold, in presentation order: at most one `per_order` plan
  // (the credit ladder — `tiers`, `displayMode`, `taxInclusive`; omitted
  // fields keep the catalog ladder) and any number of `unlimited` plans
  // (unlimited orders per truck per month — `perVehiclePricePerMonth`,
  // `trial`, optional `minTrucks` / `maxTrucks`, `name`). `trial` is
  // `{ by: 'days', days }`, `{ by: 'orders', orders }` or `{ by: 'none' }`.
  // `flow` is how the buyer is walked through it (`choose_model` asks
  // unlimited-vs-per-waybill, then which unlimited plan, then the
  // quantity; `menu` lists `menu` options, each pointing at a plan `id`
  // with a `qty` or a `vehicleCount`). `plans` replaces the catalog plans
  // (`SUBSCRIPTION_CONFIG.sales`) as a whole. Edit it from the admin panel
  // (Config → Pricing), which validates and copies the JSON.
  // A future `hero_pricing` flag will carry the same shape for Hero.
  wathiqa_plus_pricing: {
    desc: 'Wathiqa Plus pricing experiment — per-audience plans (per-order ladder + unlimited monthly plans), menu, sales flow, and signup freeCredits policy',
    status: true,
    payload: {
      org: {
        freeCredits: { qty: 1, mode: 'free' },
        flow: 'choose_model',
        plans: [
          {
            id: 'unlimited',
            kind: 'unlimited',
            name: { en: 'Unlimited', ar: 'بلا حدود' },
            perVehiclePricePerMonth: { SAR: 99, USD: 27 },
            taxInclusive: false,
          },
          { id: 'per_order', kind: 'per_order', displayMode: 'per_unit', taxInclusive: false },
        ],
      },
      freelancer: {
        freeCredits: { qty: 1, mode: 'free' },
        flow: 'menu',
        plans: [
          { id: 'per_order', kind: 'per_order', displayMode: 'per_unit', taxInclusive: false },
          {
            id: 'unlimited',
            kind: 'unlimited',
            name: { en: 'Unlimited', ar: 'بلا حدود' },
            perVehiclePricePerMonth: { SAR: 79, USD: 21 },
            taxInclusive: false,
          },
        ],
        menu: [
          { id: 'pack_5', plan: 'per_order', qty: 5 },
          { id: 'pack_10', plan: 'per_order', qty: 10, highlight: true },
          { id: 'monthly', plan: 'unlimited', vehicleCount: 1 },
        ],
      },
    },
  },

  // Wathiqa Plus reply voice v2: one conversation brief instead of the
  // per-turn system notes, history equal to what the phone showed, one
  // author per reply, the slim English prompt. Fails closed — evaluated
  // through `FeatureFlagsService.getRolloutFlag`, so no PostHog client
  // (local, CI) means v1. See ADR 028.
  wathiqa_voice_v2: {
    desc: 'Wathiqa Plus reply voice v2 — conversation brief, wire-equal history, one author per reply, slim prompt',
    status: false,
  },

  // Agent consistency rollout (docs/ai/decisions/033). Each fails closed through
  // `AgentRolloutGate`; a `shadow` variant logs what the new path would
  // do without changing the reply. Staging first, then production.
  temp_agent_wire_history: {
    desc: 'Agent history replays what the phone showed (wire) for every persona, not only voice v2',
    status: false,
  },
  temp_agent_inbound_ledger: {
    desc: 'Every inbound WhatsApp message is persisted once at ingest, keyed by wamid; turns reference message ids',
    status: false,
  },
  temp_agent_entity_resolver: {
    desc: 'Resolve drivers / lanes / numbers in each inbound line before the model and keep them in conversation memory',
    status: false,
  },
  temp_agent_ask_guard: {
    desc: 'Block replies that ask for a fact already on file or in memory (variant shadow = log only)',
    status: false,
  },
  temp_agent_name_judge: {
    desc: 'AI judges whether two written names are the same company / person (variant shadow = log only)',
    status: false,
  },
  temp_agent_multi_stop_trip: {
    desc: 'One driver to several cities: one order with ordered drop-offs and one Bayan trip with a waybill per drop-off',
    status: false,
  },
  temp_agent_party_phone_driver: {
    desc: 'Bayan sender / receiver phone falls back to the driver phone instead of the account holder',
    status: false,
  },
  temp_agent_setup_routing: {
    desc: 'A fully set-up Wathiqa Plus account gets the logisti + order specialists instead of the onboarding script (variant shadow = log only)',
    status: false,
  },

  live_apps: {
    desc: 'Per-surface availability (full-app gate outside normal shell when off)',
    status: 'enabled' as FeatureStatus,
    payload: {
      sub_carrier_app_ios: true,
      sub_carrier_app_android: true,
      sub_customer_app_ios: true,
      sub_customer_app_android: true,
      sub_dashboard: true,
      sub_admin_panel: true,
    },
  },
} as const;

export type FeatureFlagsMap = typeof FEATURE_FLAGS;
export type FeatureFlagKey = keyof FeatureFlagsMap;

/**
 * Extract valid sub-flag keys for a given flag.
 * Returns `never` for flags without a `payload` containing `sub_*` keys.
 */
type SubFlagPayloadKeys<P> = keyof P extends infer K
  ? K extends keyof P & `sub_${string}`
    ? K
    : never
  : never;

export type SubFlagKeysOf<K extends FeatureFlagKey> = FeatureFlagsMap[K] extends {
  payload: infer P;
}
  ? SubFlagPayloadKeys<P>
  : never;
